'use client'

import React, { useMemo, useCallback } from 'react'
import { Header, HeaderDataDesktop } from '@quark/header'
import { Logout, Policy, Profile } from '@quark/icons'
import { Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import Cookies from 'js-cookie'
import { logoutUser } from '@services/loginService'
import { KEY_REFRESH_TOKEN, KEY_USER_ID } from '@components/GlobalHeader/constants'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { getModifiedSectionData, openLink } from '@factories/getGlobalHeaderData'

interface DesktopHeaderProps {
  isLoggedIn: boolean
  userData?: any
  content?: any
  animate?: boolean
}

const DesktopHeader: React.FC<DesktopHeaderProps> = ({ isLoggedIn, userData, content, animate = false }) => {
  const config = useEnvVariablesClientConfig()

  const handleLogout = useCallback(async () => {
    try {
      await logoutUser({
        userId: Cookies.get(KEY_USER_ID),
        refreshToken: Cookies.get(KEY_REFRESH_TOKEN),
        env: config?.ACKO_ENV,
      })
    } catch (error) {
      console.error('Logout failed', error)
    } finally {
      Cookies.remove('refresh_token')
      Cookies.remove('user_id')
      window.location.reload()
    }
  }, [config])

  const userSection = useMemo(() => {
    if (isLoggedIn) {
      return {
        name: (
          <Flex gap="spacing-12" alignItems="center" justifyContent="center">
            <Text fontStyle="um_text-xl/normal" color="base/primary" lineHeight="line-height-0">
              <Profile />
            </Text>
            <Text fontStyle="um_text-base/normal" color="base/primary">
              {userData?.name?.split(' ')[0]}
            </Text>
          </Flex>
        ),
        type: 'outlined-button' as const,
        dropDownType: 'contentWidth' as const,
        isExpandable: true,
        props: { onClick: () => openLink('', [], '') },
        options: [
          { name: 'View Profile', icon: <Profile />, props: { onClick: () => openLink('', [], '/profile') } },
          { name: 'My Policies', icon: <Policy />, props: { onClick: () => openLink('', [], '/myaccount') } },
          {
            name: 'Logout',
            icon: <Logout />,
            props: {
              onClick: () => {
                void handleLogout()
              },
            },
          },
        ],
      }
    }

    return {
      name: 'Login',
      type: 'filled-button' as const,
      isExpandable: false,
      props: {
        onClick: () => {
          window.location.href = '/authn/v1/signin'
        },
      },
    }
  }, [isLoggedIn, userData])

  const headerData: HeaderDataDesktop = useMemo(
    () => ({
      logo: {
        darkUrl: content?.darkThemeLogoUrl,
        lightUrl: content?.lightThemeLogoUrl,
      },
      leftSection: getModifiedSectionData(content?.leftSideCategories ?? []),
      rightSection: [...getModifiedSectionData(content?.rightSideCategories ?? []), userSection],
      animate,
    }),
    [content, userSection]
  )
  return <Header headerData={headerData} animate={animate} />
}

export default React.memo(DesktopHeader)
